// ./src/utils/api/AccommodationService.ts
//
// Accommodation (event lodging options — hotels / rentals) service. OpResult-native,
// event-scoped: every list is filtered by eventId. Full single-item CRUD plus a bulk
// status change / remove (array of ids in the where clause). Talks to the
// eventAccommodation* GraphQL operations. Room photos are NOT part of this record —
// they live in ms_storage under the context path e/{eventId}/ac/{id}. See
// em-scaffold-ui.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { listAllPages, ListAllOptions } from "./listAllPages";
import { Pagination, SortItem } from "./commonApiTypes";

// =============================================================================
// GraphQL
// =============================================================================

const ACCOMMODATION_FIELDS = `
  id
  eventId
  accountId
  categoryId
  name
  description
  link
  website
  phone
  email
  streetAddress
  latitude
  longitude
  distanceMeters
  price
  currencyCode
  priceUnit
  groupBookingCode
  images {
    uploadedFileId
    url
    uploadedFile {
      id
      fileUrl
      thumbnailUrl
    }
  }
  orderNo
  status
  rights
  createdAt
  updatedAt
`;

const ACCOMMODATION_LIST_QUERY = gql`
  query EventAccommodationList($filter: EventAccommodationFilter, $params: PaginationAndSorting) {
    eventAccommodationList(filter: $filter, params: $params) {
      code
      data { ${ACCOMMODATION_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const ACCOMMODATION_GET_QUERY = gql`
  query EventAccommodationGet($id: ID) {
    eventAccommodationGet(id: $id) {
      code
      data { ${ACCOMMODATION_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const ACCOMMODATION_GET_MANY_QUERY = gql`
  query EventAccommodationGetMany($ids: [ID]) {
    eventAccommodationGetMany(ids: $ids) {
      code
      data { ${ACCOMMODATION_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const ACCOMMODATION_CREATE_MUTATION = gql`
  mutation EventAccommodationCreate($params: EventAccommodationInput) {
    eventAccommodationCreate(params: $params) {
      code
      data { ${ACCOMMODATION_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const ACCOMMODATION_UPDATE_MUTATION = gql`
  mutation EventAccommodationUpdate($where: EventAccommodationWhereInput, $params: EventAccommodationInput) {
    eventAccommodationUpdate(where: $where, params: $params) {
      code
      data { ${ACCOMMODATION_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const ACCOMMODATION_REMOVE_MUTATION = gql`
  mutation EventAccommodationRemove($where: EventAccommodationWhereInput) {
    eventAccommodationRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types (match the eventsmagic-api EventAccommodation schema)
// =============================================================================

/** Resolved ms_storage file on an accommodation image (federated from UploadedFile). */
export interface AccommodationFileRef {
  id?: string | null;
  fileUrl?: string | null;
  thumbnailUrl?: string | null;
}

/** One photo in an accommodation's flat gallery — an uploaded_file ref and/or a URL. */
export interface AccommodationImage {
  uploadedFileId?: string | null;
  url?: string | null;
  uploadedFile?: AccommodationFileRef | null;
}

export interface AccommodationImageInput {
  uploadedFileId?: string | null;
  url?: string | null;
}

/** Displayable URL for one image: resolved file URL first, else the stored URL. */
export const accommodationImageUrl = (img: AccommodationImage | null | undefined): string | null =>
  img?.uploadedFile?.fileUrl || img?.url || null;

export interface Accommodation {
  id?: string;
  eventId?: string | null;
  accountId?: string | null;
  categoryId?: string | null;
  name?: string | null;
  description?: string | null;
  link?: string | null;
  website?: string | null;
  phone?: string | null;
  email?: string | null;
  streetAddress?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  /** Straight-line distance from the event venue, in metres. */
  distanceMeters?: number | null;
  price?: number | null;
  currencyCode?: string | null;
  priceUnit?: string | null;
  /** Room-block / discount code guests quote when booking. */
  groupBookingCode?: string | null;
  /** Flat photo gallery (first entry = main). */
  images?: AccommodationImage[] | null;
  orderNo?: number | null;
  status?: number;
  /** Actions the current caller may perform ('edit' | 'remove' | 'duplicate'). */
  rights?: string[] | null;
  createdAt?: string;
  updatedAt?: string;
  /** CLIENT-ONLY: how many guest parties upvoted this stay, merged in from UpvoteCountService
   *  (not from the list query). The list shows it as a badge. */
  upvoteCount?: number;
}

export interface AccommodationInput {
  eventId?: string | null;
  categoryId?: string | null;
  name?: string | null;
  description?: string | null;
  link?: string | null;
  website?: string | null;
  phone?: string | null;
  email?: string | null;
  streetAddress?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  distanceMeters?: number | null;
  price?: number | null;
  priceUnit?: string | null;
  groupBookingCode?: string | null;
  /** Flat photo gallery (first entry = main). */
  images?: AccommodationImageInput[] | null;
  orderNo?: number | null;
  status?: number | null;
}

// A bulk update (>1 id) may only mass-change status (server-enforced).
export interface AccommodationBulkInput {
  status?: number | null;
}

export interface AccommodationFilter {
  id?: string[] | null;
  eventId?: string[] | null;
  categoryId?: string[] | null;
  status?: number[] | null;
  searchKeyword?: string | null;
}

export interface AccommodationWhereInput {
  /** One id, or many for a bulk update/remove. */
  id?: string | string[];
}

// =============================================================================
// Service
// =============================================================================

export class AccommodationService {
  static async list(
    filter?: AccommodationFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    return ApiService.executeQuery(ACCOMMODATION_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  /**
   * Every row matching `filter`, paged — for PRINT SHEETS and exports, which need the whole set.
   * ⚠ Never pass a literal `pageSize` in a print page instead of this: paper has no Load-more
   * button, so a capped sheet is indistinguishable from a complete one. See `listAllPages`.
   */
  static async listAll(filter?: AccommodationFilter, sortBy?: SortItem[], opts?: ListAllOptions): Promise<OpResult> {
    return listAllPages<Accommodation>(AccommodationService.list, filter, sortBy, opts);
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(ACCOMMODATION_GET_QUERY, { id });
  }

  static async getMany(ids: string[]): Promise<OpResult> {
    return ApiService.executeQuery(ACCOMMODATION_GET_MANY_QUERY, { ids });
  }

  static async create(params: AccommodationInput): Promise<OpResult> {
    return ApiService.executeMutation(ACCOMMODATION_CREATE_MUTATION, { params });
  }

  static async update(where: AccommodationWhereInput, params: AccommodationInput): Promise<OpResult> {
    return ApiService.executeMutation(ACCOMMODATION_UPDATE_MUTATION, { where, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(ACCOMMODATION_REMOVE_MUTATION, { where: { id } });
  }

  /** Mass-change status on every selected accommodation in one call (server restricts a multi-id update to status). */
  static async bulkUpdate(ids: string[], params: AccommodationBulkInput): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(ACCOMMODATION_UPDATE_MUTATION, { where: { id: ids }, params });
  }

  /** Remove every selected accommodation in one call. */
  static async removeMany(ids: string[]): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(ACCOMMODATION_REMOVE_MUTATION, { where: { id: ids } });
  }
}
